import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs/operators';
import { ProjectTemplateModel } from '../../models/project.model';
import { IntegrationMappingModel } from '../../models/integration.model';
import { IntegrationService } from '../../services/integration.service';
import { ProjecttemplateService } from '../../services/projecttemplate.service';
import { TEMPLATE_TYPE } from '../../models/template.constants';

type IntegrationType = 'kisha' | 'ewmr' | 'commonTags' | 'dashboard';
type DashboardTemplateCategory = 'template' | 'forms';

interface ProjectIntegrationRow {
  integrationMappingId: number;
  sourceId: string;
  kanseiId: string;
  dataType: string;
  comments: string;
  selected: boolean;
}

@Component({
  selector: 'app-project-integrations',
  standalone: true,
  imports: [CommonModule, FormsModule, ToastModule],
  providers: [MessageService],
  templateUrl: './project-integrations.html',
  styleUrl: './project-integrations.css'
})
export class ProjectIntegrationsComponent implements OnInit {
  projectId = 0;
  activeIntegration: IntegrationType = 'kisha';
  kishaProjectTemplates: ProjectTemplateModel[] = [];
  ewmrProjectTemplates: ProjectTemplateModel[] = [];
  commonTagsProjectTemplates: ProjectTemplateModel[] = [];
  dashboardProjectTemplates: ProjectTemplateModel[] = [];
  dashboardTemplateCategory: DashboardTemplateCategory = 'template';
  readonly dashboardTemplateCategoryOptions = [
    { label: 'Template', value: 'template' as DashboardTemplateCategory },
    { label: 'Forms', value: 'forms' as DashboardTemplateCategory }
  ];
  selectedKishaProjectTemplateId: number | null = null;
  selectedEwmrProjectTemplateId: number | null = null;
  selectedCommonTagsProjectTemplateId: number | null = null;
  selectedDashboardProjectTemplateId: number | null = null;
  rows: ProjectIntegrationRow[] = [];
  allSelected = false;
  loadingTemplates = false;
  loadingMappings = false;
  loadingAssociations = false;
  saving = false;

  constructor(
    private route: ActivatedRoute,
    private projecttemplateService: ProjecttemplateService,
    private integrationService: IntegrationService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    const routeProjectId = Number(this.route.snapshot.paramMap.get('projectId'));
    this.projectId = Number.isFinite(routeProjectId) && routeProjectId > 0 ? routeProjectId : 0;
    if (this.projectId <= 0) {
      return;
    }
    this.loadProjectTemplates('kisha');
    this.loadIntegrationRows();
  }

  setIntegration(type: IntegrationType): void {
    if (this.activeIntegration === type) {
      return;
    }
    this.activeIntegration = type;
    this.loadProjectTemplates(type);
    this.loadIntegrationRows();
  }

  get projectTemplates(): ProjectTemplateModel[] {
    if (this.activeIntegration === 'kisha') return this.kishaProjectTemplates;
    if (this.activeIntegration === 'ewmr') return this.ewmrProjectTemplates;
    if (this.activeIntegration === 'dashboard') return this.dashboardProjectTemplates;
    return this.commonTagsProjectTemplates;
  }

  get selectedProjectTemplateId(): number | null {
    if (this.activeIntegration === 'kisha') return this.selectedKishaProjectTemplateId;
    if (this.activeIntegration === 'ewmr') return this.selectedEwmrProjectTemplateId;
    if (this.activeIntegration === 'dashboard') return this.selectedDashboardProjectTemplateId;
    return this.selectedCommonTagsProjectTemplateId;
  }

  set selectedProjectTemplateId(value: number | null) {
    if (this.activeIntegration === 'kisha') {
      this.selectedKishaProjectTemplateId = value;
    } else if (this.activeIntegration === 'ewmr') {
      this.selectedEwmrProjectTemplateId = value;
    } else if (this.activeIntegration === 'dashboard') {
      this.selectedDashboardProjectTemplateId = value;
    } else {
      this.selectedCommonTagsProjectTemplateId = value;
    }
  }

  get sourceLabel(): string {
    if (this.activeIntegration === 'kisha') return 'Kisha ID';
    if (this.activeIntegration === 'ewmr') return 'EWMR ID';
    return '';
  }

  get idLabel(): string {
    return this.activeIntegration === 'commonTags' || this.activeIntegration === 'dashboard' ? 'Tag ID' : 'Navigator ID';
  }

  get showSourceColumn(): boolean {
    return this.activeIntegration !== 'commonTags' && this.activeIntegration !== 'dashboard';
  }

  onTemplateChange(): void {
    this.loadSelectedAssociations();
  }

  onDashboardTemplateCategoryChange(): void {
    if (this.activeIntegration !== 'dashboard') {
      return;
    }

    this.selectedDashboardProjectTemplateId = null;
    this.dashboardProjectTemplates = [];
    this.loadProjectTemplates('dashboard');
  }

  toggleSelectAll(): void {
    this.rows.forEach((row) => {
      row.selected = this.allSelected;
    });
  }

  onRowSelectionChange(): void {
    this.allSelected = this.rows.length > 0 && this.rows.every((row) => row.selected);
  }

  save(): void {
    if (!this.selectedProjectTemplateId || this.selectedProjectTemplateId <= 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'Template Required',
        detail: 'Select a project template before saving.'
      });
      return;
    }

    const payload = {
      projectTemplateId: this.selectedProjectTemplateId,
      integrationType: this.toBackendIntegrationType(this.activeIntegration),
      selectedIntegrationMappingIds: this.rows.filter((row) => row.selected).map((row) => row.integrationMappingId)
    };

    this.saving = true;
    this.integrationService.upsertProjectTemplateAssociations(payload)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Saved',
            detail: 'Project template mappings updated successfully.'
          });
          this.loadSelectedAssociations();
        },
        error: (error) => {
          console.error('Error saving project template integration mappings:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save project template mappings.'
          });
        }
      });
  }

  private loadProjectTemplates(type: IntegrationType): void {
    const templateTypeId = this.getTemplateTypeId(type);
    this.loadingTemplates = true;
    this.projecttemplateService
      .getProjectTemplatesByType(this.projectId, templateTypeId)
      .pipe(finalize(() => (this.loadingTemplates = false)))
      .subscribe({
        next: (items) => {
          const templates = (items || []).filter((item) => !!item.templateId && this.isTemplateActive(item));
          if (type === 'kisha') {
            this.kishaProjectTemplates = templates;
            if (!this.selectedKishaProjectTemplateId && templates.length > 0) {
              this.selectedKishaProjectTemplateId = templates[0].templateId ?? null;
            }
          } else if (type === 'ewmr') {
            this.ewmrProjectTemplates = templates;
            if (!this.selectedEwmrProjectTemplateId && templates.length > 0) {
              this.selectedEwmrProjectTemplateId = templates[0].templateId ?? null;
            }
          } else if (type === 'dashboard') {
            this.dashboardProjectTemplates = templates;
            if (!this.selectedDashboardProjectTemplateId && templates.length > 0) {
              this.selectedDashboardProjectTemplateId = templates[0].templateId ?? null;
            }
          } else {
            this.commonTagsProjectTemplates = templates;
            if (!this.selectedCommonTagsProjectTemplateId && templates.length > 0) {
              this.selectedCommonTagsProjectTemplateId = templates[0].templateId ?? null;
            }
          }
          if (this.activeIntegration === type) {
            this.loadSelectedAssociations();
          }
        },
        error: (error) => {
          console.error('Error loading project templates:', error);
          if (type === 'kisha') {
            this.kishaProjectTemplates = [];
            this.selectedKishaProjectTemplateId = null;
          } else if (type === 'ewmr') {
            this.ewmrProjectTemplates = [];
            this.selectedEwmrProjectTemplateId = null;
          } else if (type === 'dashboard') {
            this.dashboardProjectTemplates = [];
            this.selectedDashboardProjectTemplateId = null;
          } else {
            this.commonTagsProjectTemplates = [];
            this.selectedCommonTagsProjectTemplateId = null;
          }
        }
      });
  }

  private loadIntegrationRows(): void {
    this.loadingMappings = true;
    this.integrationService
      .getIntegrationMappings(this.toBackendIntegrationType(this.activeIntegration))
      .pipe(finalize(() => (this.loadingMappings = false)))
      .subscribe({
        next: (data) => {
          this.rows = (data || [])
            .filter((item) => !!item.integrationId)
            .map((item) => this.toRow(item));
          this.loadSelectedAssociations();
        },
        error: (error) => {
          console.error('Error loading integration mappings:', error);
          this.rows = [];
          this.allSelected = false;
        }
      });
  }

  private loadSelectedAssociations(): void {
    if (!this.selectedProjectTemplateId || this.selectedProjectTemplateId <= 0 || this.rows.length === 0) {
      this.rows = this.rows.map((row) => ({ ...row, selected: false }));
      this.allSelected = false;
      return;
    }

    this.loadingAssociations = true;
    this.integrationService
      .getProjectTemplateAssociations(this.selectedProjectTemplateId, this.toBackendIntegrationType(this.activeIntegration))
      .pipe(finalize(() => (this.loadingAssociations = false)))
      .subscribe({
        next: (data) => {
          const mappedIds = new Set((data || []).map((item) => item.integrationMappingId));
          this.rows = this.rows.map((row) => ({
            ...row,
            selected: mappedIds.has(row.integrationMappingId)
          }));
          this.allSelected = this.rows.length > 0 && this.rows.every((row) => row.selected);
        },
        error: (error) => {
          console.error('Error loading project template associations:', error);
          this.rows = this.rows.map((row) => ({ ...row, selected: false }));
          this.allSelected = false;
        }
      });
  }

  private toBackendIntegrationType(type: IntegrationType): string {
    if (type === 'commonTags') {
      return 'COMMON_TAGS';
    }
    if (type === 'dashboard') {
      return 'DASHBOARD';
    }
    return type.toUpperCase();
  }

  private toRow(item: IntegrationMappingModel): ProjectIntegrationRow {
    return {
      integrationMappingId: item.integrationId as number,
      sourceId: item.sourceId?.trim() || '',
      kanseiId: item.kanseiId?.trim() || '',
      dataType: item.dataType?.trim() || '',
      comments: item.comments?.trim() || '',
      selected: false
    };
  }

  private isTemplateActive(item: ProjectTemplateModel): boolean {
    return item.isActive === undefined || item.isActive === null || item.isActive === true;
  }

  private getTemplateTypeId(type: IntegrationType): number {
    if (type === 'ewmr') {
      return TEMPLATE_TYPE.FORMS;
    }

    if (type === 'dashboard') {
      return this.dashboardTemplateCategory === 'forms' ? TEMPLATE_TYPE.FORMS : TEMPLATE_TYPE.TEMPLATE;
    }

    return TEMPLATE_TYPE.TEMPLATE;
  }
}
